import React, { useEffect, useRef, useState } from 'react';
import AssessmentIntroduction from './AssessmentIntroduction';
import QuestionnaireWizard from 'components/questionnaire/QuestionnaireWizard';
import { Questionnaire } from 'types/questionnaire/types';
import { useNavigate } from 'react-router-dom';
import { createQuestionnaireSession, completeQuestionnaireSession } from 'features/questionnaire/api/questionnaireApi';
import { getEdTreatmentRecommendation, AnswersMap } from 'features/questionnaire/domain/edRecommendation';
import { loadEdQuestionnaire, loadEdSkus, warmEdJourney } from './journeyData';
import { ROUTES } from 'config/route-configuration';
import { writeEdFlowState } from 'features/ed-treatment/edFlowStorage';

const ErectileDysfunctionQuestionnairePage: React.FC = () => {
  const [started, setStarted] = useState(false);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const initialLoad = useRef<Promise<[Questionnaire, { sessionId: string }]> | null>(null);
  useEffect(() => {
    let active = true;
    void warmEdJourney();
    initialLoad.current ??= Promise.all([loadEdQuestionnaire(), createQuestionnaireSession('ed-v2')]);
    const request = initialLoad.current;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [q, session] = await request;
        if (!active) return;
        setQuestionnaire(q);
        setSessionId(session.sessionId);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load questionnaire', err);
        if (active) setError('Failed to load questionnaire. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const handleComplete = async (answers: Record<string, unknown>) => {
    if (!sessionId) {
      setError('No questionnaire session. Please refresh and try again.');
      return;
    }

    setSubmitting(true);
    try {
      const recommendation = getEdTreatmentRecommendation(answers as AnswersMap);

      if (recommendation) void loadEdSkus(recommendation.treatmentKey).catch(() => null);
      await completeQuestionnaireSession({
        sessionId,
        questionnaireId: 'ed-v2',
        answers,
        recommendedDrugId: recommendation?.treatmentKey,
      });

      writeEdFlowState({
        sessionId,
        questionnaireId: 'ed-v2',
        recommendation,
      });

      navigate(ROUTES.EDRESULTS, {
        state: {
          sessionId,
          questionnaireId: 'ed-v2',
          answers,
          recommendation,
        },
      });
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error(err);
      setError('Failed to submit questionnaire. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="ed-questionnaire flex min-h-screen flex-col bg-talo-blue-100 px-4 md:px-10 py-5 md:py-10 rounded-3xl">
      {!started && <AssessmentIntroduction onStart={() => setStarted(true)} />}

      {started && (
        <div className="ed-questionnaire-card mx-auto w-full max-w-4xl rounded-2xl bg-white p-10 shadow-sm">
          {loading && <p>Loading questionnaire...</p>}
          {error && <p className="text-red-600">{error}</p>}
          {!loading && !error && questionnaire && sessionId && (
            <QuestionnaireWizard questionnaire={questionnaire} onComplete={handleComplete} isSubmitting={submitting} />
          )}
          {!loading && !error && (!questionnaire || !sessionId) && (
            <p className="text-red-600">Unable to start questionnaire. Please try again.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ErectileDysfunctionQuestionnairePage;
